import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, X, Search, Users as UsersIcon, Plus, ArrowRight } from "lucide-react";
import api from "../api";
import { useAuth } from "../AuthContext";
import { t, LOCALES } from "../i18n";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

function isOnline(u) {
    if (!u?.last_seen) return false;
    return (Date.now() - new Date(u.last_seen).getTime()) / 1000 < 90;
}

// Sound: short embedded ding (base64 wav, ~50ms ping)
const DING_DATA_URI = "data:audio/wav;base64,UklGRiQEAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAEAACAgIB+gIB+f4B/foCAfn5/gH9+f4F/f3+Af4CAgIB/gH+AgIB/gIB/gIB/gIB/gIB/gIB/gH9/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/gIB/";

export default function FloatingChatWidget() {
    const { lang, user } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [chats, setChats] = useState([]);
    const [search, setSearch] = useState("");
    const [totalUnread, setTotalUnread] = useState(0);
    const audioRef = useRef(null);
    const lastUnreadRef = useRef(0);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/chats");
            setChats(data);
            const total = data.reduce((s, c) => s + (c.unread_count || 0), 0);
            // Play sound when unread increases
            if (total > lastUnreadRef.current && lastUnreadRef.current >= 0) {
                try { audioRef.current?.play().catch(() => {}); } catch {}
                // Browser notification
                if ("Notification" in window && Notification.permission === "granted") {
                    const newest = data.find((c) => (c.unread_count || 0) > 0 && c.last_message);
                    if (newest) {
                        const n = new Notification(`💬 ${newest.display_name}`, {
                            body: newest.last_message?.text?.slice(0, 80) || "Neue Nachricht",
                            icon: "/favicon.ico",
                            tag: newest.id,
                        });
                        n.onclick = () => { window.focus(); navigate(`/chat?chat=${newest.id}`); };
                    }
                }
            }
            lastUnreadRef.current = total;
            setTotalUnread(total);
        } catch {}
    }, [navigate]);

    useEffect(() => {
        if (!user) return;
        load();
        // Request browser notification permission once
        if ("Notification" in window && Notification.permission === "default") {
            Notification.requestPermission().catch(() => {});
        }
        const id = setInterval(load, 8000);
        return () => clearInterval(id);
    }, [user, load]);

    if (!user) return null;

    const filtered = chats.filter((c) => !search || (c.display_name || "").toLowerCase().includes(search.toLowerCase()));

    return (
        <>
            <audio ref={audioRef} src={DING_DATA_URI} preload="auto" />
            {/* Floating Button */}
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    data-testid="floating-chat-btn"
                    aria-label="Chat"
                    className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-xl flex items-center justify-center transition-all hover:scale-110"
                >
                    <MessageSquare className="w-6 h-6" />
                    {totalUnread > 0 && (
                        <span data-testid="floating-chat-unread" className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center border-2 border-white">
                            {totalUnread > 99 ? "99+" : totalUnread}
                        </span>
                    )}
                </button>
            )}

            {/* Popup panel */}
            {open && (
                <div
                    data-testid="floating-chat-panel"
                    className="fixed bottom-6 right-6 z-50 w-80 sm:w-96 max-h-[70vh] bg-white rounded-xl shadow-2xl border border-neutral-200 flex flex-col animate-in slide-in-from-bottom-4"
                >
                    <header className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between bg-blue-600 text-white rounded-t-xl">
                        <h3 className="font-semibold flex items-center gap-2">
                            <MessageSquare className="w-4 h-4" /> {t(lang, "chat")}
                        </h3>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => { navigate("/chat"); setOpen(false); }}
                                data-testid="floating-chat-expand"
                                title={lang === "de" ? "Vollbild öffnen" : "Open fullscreen"}
                                className="w-7 h-7 rounded hover:bg-blue-700 flex items-center justify-center"
                            >
                                <ArrowRight className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setOpen(false)}
                                data-testid="floating-chat-close"
                                className="w-7 h-7 rounded hover:bg-blue-700 flex items-center justify-center"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </header>
                    <div className="p-2 border-b border-neutral-100">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t(lang, "search")}
                                className="h-8 pl-8 text-xs"
                                data-testid="floating-chat-search"
                            />
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {filtered.length === 0 ? (
                            <p className="p-6 text-center text-xs text-neutral-500">{t(lang, "noChats")}</p>
                        ) : filtered.map((c) => {
                            const online = !c.is_group && isOnline(c.other_user);
                            return (
                                <button
                                    key={c.id}
                                    onClick={() => { navigate(`/chat?chat=${c.id}`); setOpen(false); }}
                                    data-testid={`floating-chat-item-${c.id}`}
                                    className="w-full flex items-start gap-2.5 p-3 hover:bg-neutral-50 border-b border-neutral-100 text-left"
                                >
                                    <div className="relative shrink-0">
                                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-xs ${c.is_group ? "bg-blue-600" : "bg-neutral-900"}`}>
                                            {c.is_group ? <UsersIcon className="w-4 h-4" /> : (c.display_name || "?").slice(0, 2).toUpperCase()}
                                        </div>
                                        {!c.is_group && (
                                            <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${online ? "bg-emerald-500" : "bg-neutral-300"}`} />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between">
                                            <p className="font-semibold text-sm truncate">{c.display_name}</p>
                                            {c.last_message && (
                                                <span className="text-[10px] text-neutral-400 shrink-0 ml-1">
                                                    {new Date(c.last_message.created_at).toLocaleTimeString(LOCALES[lang], { hour: "2-digit", minute: "2-digit" })}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-neutral-500 truncate">
                                            {c.last_message ? `${c.last_message.user_name}: ${c.last_message.text}` : <span className="italic">—</span>}
                                        </p>
                                    </div>
                                    {c.unread_count > 0 && (
                                        <span className="bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">{c.unread_count}</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    <footer className="p-2 border-t border-neutral-100 flex items-center justify-between text-xs">
                        <button onClick={() => { navigate("/chat"); setOpen(false); }} data-testid="floating-chat-new" className="px-2 py-1 rounded hover:bg-neutral-100 text-blue-700 flex items-center gap-1">
                            <Plus className="w-3 h-3" /> {t(lang, "newChat")}
                        </button>
                        <span className="text-neutral-400">{chats.length} {t(lang, "chat").toLowerCase()}</span>
                    </footer>
                </div>
            )}
        </>
    );
}
